import React, { useCallback, useState } from 'react';
import { Query } from '../_common';
import SearchBar from '../SearchBar';
import { SearchTypeEnum } from '../../types/SearchTypeEnum';
import { ModalForm, Option } from './interface';
import { useAuthContext } from '../Auth/AuthProvider';
import { useStoreContext } from '../App/StoreProvider';
import { PermissionEnum } from '../../types/PermissionEnum';
import { GET_ALL_LOFAS_FILTERED, GET_ALL_STATES, GET_ALL_REGIONS } from '../../graphql/client/queries';
import {
    GetAllLofasFiltered,
    GetAllStates,
    GetAllStates_getAllStates as StateType,
    GetAllRegions,
    GetAllRegions_getAllRegions as RegionType,
} from '../../types/schemaTypes';
import { Error, Loading } from '../_common';
import { useMutation, useQuery } from '@apollo/client/react';
import { SAVE_LOFA, DELETE_LOFA } from '../../graphql/client/mutations';
import './Lofas.scss';
import {
    GetAllLofasFiltered_getAllLofasFiltered as Lofa,
    GetAllLofasFiltered_getAllLofasFiltered_polygonCoordinates as LatLong,
} from '../../types/schemaTypes';
import LofaList from './LofaList';
import { ToastContainer, toast } from 'react-toastify';
import { toastOpt } from '../../utils/helpers/toastHelper'
import { SaveLofa } from '../../types/schemaTypes';
import AddLofaModal from './AddEditLofaModal/AddEditLofaModal';

interface LatLng {
    lat: number;
    lng: number;
}

const Messages = {
    delete: 'Lofa deleted successfully',
};
const ErrorMessages = {
    delete: 'Failed to delete Lofa',
};


const PAGE_SIZE = 50;

const Lofas = (): React.ReactElement => {
    const canEditLofa = useAuthContext().userHasPermission(PermissionEnum.EditLOFA);
    document.title = 'SBS';

    const [state] = useStoreContext();
    const {
        lofaList: { searchPhrase },
    } = state;
    const variables = {
        input: searchPhrase,
    };

    const [errorMessage, setErrorMessage] = useState('');
    const [showAddLofaModal, setShowAddLofaModal] = useState(false);
    const [modalForm, setModalForm] = useState(ModalForm.EMPTY);
    const [isEndOfResults, setIsEndOfResults] = useState(false);
    const [selectedLofa,setSelectedLofa] = useState<Lofa>()
    
    const onCompletedLofa = (data: SaveLofa): void => {
        if (data.saveLofa === null) return;
        const saveLofaErrors = data.saveLofa.errors;
        if (saveLofaErrors && saveLofaErrors.length > 0) {
            setErrorMessage(saveLofaErrors[0] || '');
        } else {
            setSelectedLofa(undefined);
            setErrorMessage('');
            setShowAddLofaModal(false);
            setErrorMessage('');
        }
    };
    const onCompleted = action => {
        toast.success(Messages[action], toastOpt);
    };
    const onActionError = action => {
        toast.error(ErrorMessages[action], toastOpt);
    };
    const [saveLofa] = useMutation<SaveLofa>(SAVE_LOFA, {
        onCompleted: onCompletedLofa,
        onError: (err) => setErrorMessage(err.message || ''),
        refetchQueries: [
            {
                query: GET_ALL_LOFAS_FILTERED,
                variables: { variables },
            },
        ]
    });

    const [deleteLofa] = useMutation(DELETE_LOFA, {
        onCompleted: () => onCompleted('delete'),
        onError: () => onActionError('delete'),
        refetchQueries: () => {
            return [
                {
                    query: GET_ALL_LOFAS_FILTERED,
                    variables: { variables },
                },
            ];
        },
    });

    const onDelete = useCallback(async (record: any) => {
        const variables = { input: { id: record.id } };
        await deleteLofa({ variables });
    }, []);

    const handleOnHide = (): void => {
        setSelectedLofa(undefined);
        setErrorMessage('');
        setShowAddLofaModal(false);
    };

    const openGoogleMaps = (lat: number, lng: number, lofaName: string): void => {
        const url = 'https://maps.google.com/?q=' + lat + ',' + lng;
        const newTab = window.open(url);
        setTimeout(function (): void {
            if (newTab) {
                newTab.document.title = lofaName!;
            }
        }, 100);
    };

    const handleOnSave = async (
        id: number,
        lofaName: string,
        description: string,
        stateId: number,
        regionId: number,
        districtId: number,
        city: string,
        utm: string,
        addressStreet: string,
        directions: string,
        phoneNumber: string,
        jdeNumber: string,
        longitude: number,
        latitude: number,
        entryKioskLatitude: number | null,
        entryKioskLongitude: number | null,
        entryKioskGeoFenceType: string,
        zip: string,
        geoFenceType: string,
        geoFenceRadius: number,
        polygonCoordinates: LatLng[],
        kioskGeoFenceRadius: number,
        entryKioskPolygonCoordinates: LatLng[],
    ): Promise<void> => {
        const variables = {
            input: {
                id,
                lofaName,
                addressStreet,
                city,
                stateId,
                regionId,
                districtId,
                utm,
                latitude,
                longitude,
                entryKioskLatitude,
                entryKioskLongitude,
                entryKioskGeoFenceType,
                phoneNumber,
                jdeNumber,
                directions,
                zip,
                description,
                geoFenceRadius,
                geoFenceType,
                polygonCoordinates,
                kioskGeoFenceRadius,
                entryKioskPolygonCoordinates,
            },
        };
        if (!selectedLofa) {
            const nextSelectedLofa = {
                __typename: 'LofaFiltered',
                id: id,
                lofaName: lofaName,
                addressStreet: addressStreet,
                city: city,
                stateId: stateId,
                regionId: regionId,
                districtId: districtId,
                utm: utm,
                latitude: latitude,
                longitude: longitude,
                entryKioskLatitude: entryKioskLatitude,
                entryKioskLongitude: entryKioskLongitude,
                phoneNumber: phoneNumber,
                jdeNumber: jdeNumber,
                directions: directions,
                zip: zip,
                description: description,
                geoFenceRadius: geoFenceRadius,
                geoFenceType: geoFenceType,
                polygonCoordinates: polygonCoordinates.map(polygonCoordinate => {
                    return {
                        __typename: "polygnCoordinates",
                        lat: polygonCoordinate.lat,
                        lng: polygonCoordinate.lng,
                    }
                }),
                entryKioskPolygonCoordinates: entryKioskPolygonCoordinates.map((polygonCoordinate) => {
                    return {
                        __typename: 'polygnCoordinates',
                        lat: polygonCoordinate.lat,
                        lng: polygonCoordinate.lng,
                    };
                }),
                kioskGeoFenceRadius: kioskGeoFenceRadius,
                entryKioskGeoFenceType: entryKioskGeoFenceType,
                isTCSEnabled: false,
            } as Lofa;
            setSelectedLofa(nextSelectedLofa);
        }
        await saveLofa({ variables });
    };
    const { data: statesData } = useQuery<GetAllStates>(GET_ALL_STATES);
    const { data: regionsData } = useQuery<GetAllRegions>(GET_ALL_REGIONS);
    const options: Option[] = [];
    const regions: Option[] = [];
    let regionsWithDistricts: RegionType[] = [];
    if (statesData?.getAllStates) {
        (statesData.getAllStates as (StateType | null)[]).forEach((element: StateType | null): void => {
            if (element != null && element.stateName != null && element.id != null) {
                options.push({
                    id: element.id,
                    name: element.stateName,
                });
            }
        });
    }
    if (regionsData?.getAllRegions) {
        regionsWithDistricts = (regionsData.getAllRegions ?? []).filter((r): r is RegionType => r != null);
        (regionsData.getAllRegions as (RegionType | null)[]).forEach((element: RegionType | null): void => {
            if (element != null && element.regionName != null) {
                regions.push({
                    id: element.id,
                    name: element.regionName,
                });
            }
        });
    }

    document.title = 'Lofa';

    return (
        <Query<GetAllLofasFiltered> query={GET_ALL_LOFAS_FILTERED} variables={variables}>
            {({
                error,
                data,
                loading,
                fetchMore,
            }): React.ReactElement | null => {

                const offset = data && data.getAllLofasFiltered ? data.getAllLofasFiltered.length / PAGE_SIZE : 0;

                React.useEffect(() => {
                    if (
                        offset % 1 !== 0 ||
                        (!isEndOfResults && data && data.getAllLofasFiltered && data.getAllLofasFiltered.length < PAGE_SIZE)
                    ) {
                        setIsEndOfResults(true);
                    }
                }, [offset])

                if (loading) {
                    return <Loading />;
                }

                if (error) {
                    return <Error error={error} />;
                }

                const lofas: Lofa[] = [];

                if (data && data.getAllLofasFiltered && data.getAllLofasFiltered.length !== 0) {
                    (data.getAllLofasFiltered as (Lofa | null)[]).forEach((element: Lofa | null): void => {
                        if (element != null) {
                            lofas.push(element);
                        }
                    });
                }

                return (
                    <div className="Lofas-Container">
                        <ToastContainer />
                        <div className="Lofas-Top-Container">
                            <SearchBar type={SearchTypeEnum.Lofa} />
                            <div className="Lofas-RightOptions">
                                {canEditLofa && (
                                    <div
                                        className="Lofas-Create-New-Lofa Row"
                                        onClick={() => {
                                            setShowAddLofaModal(true);
                                            setModalForm(ModalForm.ADD_LOFA);
                                        }}
                                    >
                                        <i className="material-icons Lofas-Icon">add_circle</i>
                                        <span className="Lofas-Link">Create New Lofa</span>
                                    </div>
                                )}
                            </div>
                            <div className="Lofas-Erors">{errorMessage}</div>
                        </div>
                        <div className="Lofas-Bottom-Container">
                            <div className="Lofas-Header-Title-Lg">Lofa Name</div>
                            <div className="Lofas-Header-Title-Lg">Address Street</div>
                            <div className="Lofas-Header-Title">City</div>
                            <div className="Lofas-Header-Title">Region</div>
                            <div className="Lofas-Header-Title">District</div>
                            <div className="Lofas-Header-Title-Sm">Zip</div>
                            <div className="Lofas-Header-Title">JDENumber</div>
                            <div className="Lofas-Header-Title-Lg">Action</div>
                        </div>

                        {lofas.length > 0 && (
                            <LofaList
                                lofas={lofas}
                                regionsWithDistricts={regionsWithDistricts}
                                isLoading={loading}
                                canEditLofas={canEditLofa}
                                onCLickLofa={openGoogleMaps}
                                onEditClick={Lofa => {
                                    setShowAddLofaModal(true);
                                    setModalForm(ModalForm.EDIT_LOFA);
                                    setSelectedLofa(Lofa)
                                }}
                                onDelete={onDelete}
                                isEndOfResults={isEndOfResults}
                                onLoadMore={() =>
                                    fetchMore({
                                        variables: {
                                            offset,
                                        },
                                        updateQuery: (prev, { fetchMoreResult }) => {
                                            if (
                                                prev &&
                                                fetchMoreResult &&
                                                prev.getAllLofasFiltered &&
                                                fetchMoreResult.getAllLofasFiltered
                                            ) {
                                                return Object.assign({}, prev, {
                                                    getAllLofasFiltered: [
                                                        ...prev.getAllLofasFiltered,
                                                        ...fetchMoreResult.getAllLofasFiltered,
                                                    ],
                                                });
                                            } else {
                                                return prev;
                                            }
                                        },
                                    })
                                }
                            />
                        )}

                        <AddLofaModal
                            show={showAddLofaModal}
                            modalForm={modalForm}
                            regionsWithDistricts={regionsWithDistricts}
                            regions={regions}
                            options={options}
                            selectedLofa={selectedLofa}
                            onClickHide={handleOnHide}
                            onClickSave={handleOnSave}
                            errorMessage={errorMessage}
                        />
                    </div>
                );
            }}
        </Query>
    );
};

export default Lofas;
